/**
 * Minimal promise wrapper over IndexedDB.
 *
 * Hand-rolled rather than pulling in a wrapper library: the surface we need is
 * get/put/delete/getAll over two stores, and staying dependency-free keeps the
 * public bundle smaller. See DECISIONS.md D-014.
 */

export const DB_NAME = "dungeon-builder";
export const DB_VERSION = 1;
export const MAPS_STORE = "maps";
export const APP_STORE = "app";

let databasePromise: Promise<IDBDatabase> | null = null;

const promisify = <T>(request: IDBRequest<T>): Promise<T> =>
  new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

export const openDatabase = (): Promise<IDBDatabase> => {
  if (databasePromise) return databasePromise;

  databasePromise = new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("IndexedDB is unavailable in this browser."));
      return;
    }
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(MAPS_STORE)) {
        const maps = db.createObjectStore(MAPS_STORE, { keyPath: "id" });
        maps.createIndex("updatedAt", "updatedAt");
      }
      if (!db.objectStoreNames.contains(APP_STORE)) {
        db.createObjectStore(APP_STORE, { keyPath: "key" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  // Never cache a rejected promise: a transient failure would poison every
  // later call for the lifetime of the page.
  databasePromise.catch(() => {
    databasePromise = null;
  });

  return databasePromise;
};

/**
 * Runs `work` inside one transaction and resolves only once the transaction
 * has actually committed, so a caller that awaits a write can trust the write
 * landed rather than merely being queued.
 */
export const withStore = async <T>(
  storeName: string,
  mode: IDBTransactionMode,
  work: (store: IDBObjectStore) => Promise<T> | T,
): Promise<T> => {
  const db = await openDatabase();
  return new Promise<T>((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    let result: T;
    let settled = false;

    transaction.oncomplete = () => resolve(result);
    transaction.onerror = () => {
      if (!settled) reject(transaction.error);
    };
    transaction.onabort = () => {
      if (!settled) reject(transaction.error ?? new Error("Transaction aborted."));
    };

    Promise.resolve(work(transaction.objectStore(storeName)))
      .then((value) => {
        result = value;
      })
      .catch((error: unknown) => {
        settled = true;
        try {
          transaction.abort();
        } catch {
          // Already finished; the rejection below is what matters.
        }
        reject(error);
      });
  });
};

export const getRecord = <T>(storeName: string, key: IDBValidKey): Promise<T | undefined> =>
  withStore(storeName, "readonly", (store) => promisify<T | undefined>(store.get(key)));

export const getAllRecords = <T>(storeName: string): Promise<T[]> =>
  withStore(storeName, "readonly", (store) => promisify<T[]>(store.getAll()));

export const putRecord = <T>(storeName: string, value: T): Promise<void> =>
  withStore(storeName, "readwrite", async (store) => {
    await promisify(store.put(value));
  });

export const deleteRecord = (storeName: string, key: IDBValidKey): Promise<void> =>
  withStore(storeName, "readwrite", async (store) => {
    await promisify(store.delete(key));
  });

/** Test hook: drops the cached connection so a fresh fake-indexeddb can be used. */
export const resetDatabaseConnection = () => {
  databasePromise = null;
};
